import { Grid } from '@mui/material';
import { GridRowModes } from '@mui/x-data-grid-pro';
import { Parser } from 'expr-eval';
import { Form, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';

import { mapGridIndexes, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { GridCell } from '@shared/components/grid/GridCell';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from '@shared/components/modals/Modal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { IMap } from '@shared/interfaces';
import { Wrapper } from '@styles/CommonStyles';

import { formulaFormValidationSchema } from '../../../validation';
import { getCountyString } from './DocFeesTable';
import { FormulaForm } from './FormulaForm';
import { calculationType, formulaExamples, formulaVariables, IFormulaFields, IFormulaProps, IFormulaVariables } from './interfaces';
import { requestCreateDocFee, requestUpdateDocFee } from './queries';

export const variablesColumns = [
  dataGridCol('id', {
    headerName: 'Path',
    width: 100,
  }),
  dataGridCol('lenderVariable', {
    flex: 1,
    textWrap: true,
  }),
  dataGridCol('testValue', {
    headerName: 'Test Values',
    width: 120,
    editable: true,
    renderEditCell: (params) => <GridColNumber decimalScale={null} params={params} />,
    renderCell: (params) => <GridCell text={params.value ?? ''} />,
  })
].map((column) => ({ ...column, resizable: false }));

export const exampleColumns = [
  dataGridCol('description', {
    textWrap: true,
  }),
  dataGridCol('formula', {
    textWrap: true,
  }),
].map((column) => ({ ...column, flex: 1, resizable: false }));

export const parser = new Parser({
  operators: {
    // These default to true, but are included to be explicit
    add: true,
    concatenate: true,
    conditional: true,
    divide: true,
    factorial: true,
    multiply: true,
    power: true,
    remainder: true,
    subtract: true,

    // Disable and, or, not, <, ==, !=, etc.
    logical: true,
    comparison: true,

    // Disable 'in' and = operators
    in: false,
    assignment: false,
  },
});

parser.functions.if = (expr: any, thenVal: any, elseVal: any) => {
  return expr ? thenVal : elseVal;
};

export const FormulaModal = ({ modalData, handleClose, lenderId, setLoading, loading, apiRef }: IFormulaProps) => {
  const [variables, setVariables] = useState<IFormulaVariables[]>(formulaVariables);
  const intialValues: IFormulaFields = {
    id: modalData?.id ?? '',
    state: modalData?.state ?? '',
    condition: {
      minimumLoanAmount: modalData?.condition?.minimumLoanAmount ?? 0,
      maximumLoanAmount: modalData?.condition?.maximumLoanAmount ?? 0,
      maximumFeeAmount: modalData?.condition?.maximumFeeAmount ?? 0,
    },
    formula: modalData?.formula ?? '',
    counties: getCountyString(modalData?.countyList ?? []) ?? 'All',
    calculationType: calculationType.Formula,
    amount: 0,
  };

  const varValues = useMemo(() => {
    const obj = {} as IMap<number>;
    variables.forEach((item) => {
      obj[item.id] = item.testValue;
    });

    return obj;
  }, [variables]);

  const isValidFormula = useCallback((formula: string) => {
    try {
      const exp = formula;
      const expr = parser.parse(exp);

      const value = expr.evaluate(varValues);

      return !!value || value === 0;
    } catch {
      Notistack.generalError('Invalid Formula');

      return false;
    }
  }, [varValues]);

  const handleSave = useCallback((row: IFormulaVariables) => {
    const list = variables;
    const updatedList = list.map((item) => item.id === row.id ? { ...row, testValue: Number(row?.testValue ?? 0) } : item);
    setVariables(updatedList);
  }, [variables]);

  const handleSubmit = useCallback(async (data: IFormulaFields) => {
    const isValid = isValidFormula(data.formula);

    if (isValid) {
      setLoading(true);
      const payload = { ...modalData, ...data };
      const id = payload?.id ?? newGridRecordId;
      const editableRowMode = apiRef.current.getRowMode(id) === GridRowModes.Edit;

      payload?.id === newGridRecordId
        ? await requestCreateDocFee(lenderId, { ...payload, isDocFee: !!payload.isDocFee, countyIds: payload?.countyIds ?? [] })
        : await requestUpdateDocFee(lenderId, { ...payload, countyIds: payload.countyIds ?? [] });
      setLoading(false);
      editableRowMode && apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
      handleClose();
    }
  }, [modalData, lenderId, isValidFormula]);

  const onClose = useCallback(() => {
    const id = modalData?.id ?? newGridRecordId;

    apiRef.current.setEditCellValue({ id, field: 'calculationType', value: null});
    handleClose();
  }, []);

  return (
    <Modal
      hideBackdrop
      title="Add / Edit Formula"
      open={!!modalData}
      dialogMinHeight="750px"
      dialogMinWidth="450px"
      onClose={onClose}
    >
      <Grid
        container
        marginTop={2}
        justifyContent="space-between"
      >
        <Grid
          item
          xs={6}
        >
          <Formik
            enableReinitialize
            initialValues={intialValues}
            validationSchema={formulaFormValidationSchema}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <FormulaForm varValues={varValues} loading={loading} handleClose={onClose} {...props} />
              </Form>
            )}
          </Formik>
        </Grid>
        <Grid
          item
          xs={6}
          mt="-10px"
        >
          <Wrapper>
            <PhoenixGrid
              noButton
              withHeader
              autoHeight
              hideFooter
              canNotCopy
              isFlexible
              autoRowHeight
              disableSelectionOnClick
              actions={{ onSave: handleSave }}
              name="Formula Variable"
              pageSize={15}
              columns={variablesColumns}
              rows={variables}
            />
          </Wrapper>

          <Wrapper>
            <PhoenixGrid
              noButton
              withHeader
              autoHeight
              hideFooter
              isFlexible
              autoRowHeight
              disableSelectionOnClick
              name="Formula Example"
              pageSize={15}
              columns={exampleColumns}
              rows={mapGridIndexes(formulaExamples)}
            />
          </Wrapper>
        </Grid>
      </Grid>
    </Modal>
  );
};
